import { Injectable } from '@angular/core';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import dayjs, { Dayjs } from 'dayjs/esm';
import customParseFormat from 'dayjs/esm/plugin/customParseFormat';
import localeData from 'dayjs/esm/plugin/localeData';
import localizedFormat from 'dayjs/esm/plugin/localizedFormat';
import utc from 'dayjs/esm/plugin/utc';
import isSameOrAfter from 'dayjs/esm/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/esm/plugin/isSameOrBefore';
import duration from 'dayjs/esm/plugin/duration';

// Extend dayjs with required plugins
dayjs.extend(customParseFormat);
dayjs.extend(localeData);
dayjs.extend(localizedFormat);
dayjs.extend(utc);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(duration);

@Injectable()
export class DayjsDateAdapter extends DateAdapter<Dayjs> {
  private localeData = dayjs.localeData();

  constructor() {
    super();
    this.setLocale(dayjs.locale());
  }

  override setLocale(locale: string): void {
    super.setLocale(locale);
    dayjs.locale(locale);
    this.localeData = dayjs.localeData();
  }

  getYear(date: Dayjs): number {
    return date.year();
  }

  getMonth(date: Dayjs): number {
    return date.month();
  }

  getDate(date: Dayjs): number {
    return date.date();
  }

  getDayOfWeek(date: Dayjs): number {
    return date.day();
  }

  getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    if (style === 'long') {
      return this.localeData.months();
    }
    return this.localeData.monthsShort();
  }

  getDateNames(): string[] {
    return Array.from({ length: 31 }, (_, i) => String(i + 1));
  }

  getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    if (style === 'long') {
      return this.localeData.weekdays();
    }
    if (style === 'short') {
      return this.localeData.weekdaysShort();
    }
    return this.localeData.weekdaysMin();
  }

  getYearName(date: Dayjs): string {
    return String(date.year());
  }

  getFirstDayOfWeek(): number {
    return this.localeData.firstDayOfWeek();
  }

  getNumDaysInMonth(date: Dayjs): number {
    return date.daysInMonth();
  }

  clone(date: Dayjs): Dayjs {
    return date.clone();
  }

  createDate(year: number, month: number, date: number): Dayjs {
    return dayjs().year(year).month(month).date(date).startOf('day');
  }

  today(): Dayjs {
    return dayjs();
  }

  parse(value: any, parseFormat: string | string[]): Dayjs | null {
    if (value && typeof value === 'string') {
      const formats = Array.isArray(parseFormat) ? parseFormat : [parseFormat];
      for (const format of formats) {
        const parsed = dayjs(value, format, true);
        if (parsed.isValid()) {
          return parsed;
        }
      }
      // Try parsing without strict mode
      const parsed = dayjs(value);
      return parsed.isValid() ? parsed : null;
    }
    if (value instanceof Date) {
      return dayjs(value);
    }
    if (dayjs.isDayjs(value)) {
      return value.clone();
    }
    return value ? dayjs(value) : null;
  }

  format(date: Dayjs, displayFormat: string): string {
    if (!this.isValid(date)) {
      throw Error('DayjsDateAdapter: Cannot format invalid date.');
    }
    return date.format(displayFormat);
  }

  addCalendarYears(date: Dayjs, years: number): Dayjs {
    return date.add(years, 'year');
  }

  addCalendarMonths(date: Dayjs, months: number): Dayjs {
    return date.add(months, 'month');
  }

  addCalendarDays(date: Dayjs, days: number): Dayjs {
    return date.add(days, 'day');
  }

  toIso8601(date: Dayjs): string {
    return date.toISOString();
  }

  isDateInstance(obj: any): boolean {
    return dayjs.isDayjs(obj);
  }

  isValid(date: Dayjs): boolean {
    return date?.isValid() ?? false;
  }

  invalid(): Dayjs {
    return dayjs(null as any);
  }

  override deserialize(value: any): Dayjs | null {
    if (value == null || (typeof value === 'string' && value === '')) {
      return null;
    }
    if (dayjs.isDayjs(value)) {
      return value.clone();
    }
    if (value instanceof Date) {
      return dayjs(value);
    }
    if (typeof value === 'string') {
      // Try ISO format first
      const parsed = dayjs(value);
      if (parsed.isValid()) {
        return parsed;
      }
    }
    return null;
  }
}

export const DAYJS_DATE_FORMATS = {
  parse: {
    dateInput: 'DD.MM.YYYY',
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};
